import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, HomeIcon, EditIcon, TrashIcon, ConstructionIcon, PlusIcon, CalendarIcon, CheckIcon, XIcon, ClockIcon, DollarSignIcon, ListTodoIcon, PencilIcon, PackageIcon, FileTextIcon, ChevronRightIcon, AlertCircleIcon, ExternalLinkIcon, BuildingIcon, WrenchIcon, BellIcon, PhoneIcon, ReceiptIcon, ClipboardIcon, ShieldIcon, ZapIcon, DropletIcon, TrashIcon as TrashCanIcon, WifiIcon, KeyIcon, UserIcon, CalendarDaysIcon, ActivityIcon, CameraIcon, FileIcon, PlusCircleIcon } from 'lucide-react';
import Button from './common/Button';
import ProjectCard from './common/ProjectCard';
import { mockProperties, mockProjects } from '../data/mockData';
// Mock data for property schedule
const mockPropertySchedule = [{
  id: 'phase1',
  name: 'Property Acquisition',
  startDate: '2023-01-01',
  endDate: '2023-02-15',
  status: 'completed',
  tasks: [{
    name: 'Property Search',
    startDate: '2023-01-01',
    endDate: '2023-01-15',
    status: 'completed'
  }, {
    name: 'Due Diligence',
    startDate: '2023-01-15',
    endDate: '2023-01-30',
    status: 'completed'
  }, {
    name: 'Closing',
    startDate: '2023-01-30',
    endDate: '2023-02-15',
    status: 'completed'
  }]
}, {
  id: 'phase2',
  name: 'Initial Property Assessment',
  startDate: '2023-02-16',
  endDate: '2023-03-15',
  status: 'completed',
  tasks: [{
    name: 'Property Inspection',
    startDate: '2023-02-16',
    endDate: '2023-02-28',
    status: 'completed'
  }, {
    name: 'Environmental Assessment',
    startDate: '2023-03-01',
    endDate: '2023-03-10',
    status: 'completed'
  }, {
    name: 'Development Planning',
    startDate: '2023-03-10',
    endDate: '2023-03-15',
    status: 'completed'
  }]
}, {
  id: 'phase3',
  name: 'Renovation Projects',
  startDate: '2023-03-15',
  endDate: '2023-09-30',
  status: 'in-progress',
  tasks: [{
    name: 'Apartment Units Renovation',
    startDate: '2023-03-15',
    endDate: '2023-09-30',
    status: 'in-progress'
  }, {
    name: 'Common Area Improvements',
    startDate: '2023-04-01',
    endDate: '2023-07-31',
    status: 'in-progress'
  }, {
    name: 'Exterior Updates',
    startDate: '2023-05-01',
    endDate: '2023-08-15',
    status: 'not-started'
  }]
}, {
  id: 'phase4',
  name: 'Leasing & Management',
  startDate: '2023-06-01',
  endDate: '2023-12-31',
  status: 'not-started',
  tasks: [{
    name: 'Marketing Campaign',
    startDate: '2023-06-01',
    endDate: '2023-08-31',
    status: 'not-started'
  }, {
    name: 'Tenant Screening',
    startDate: '2023-07-01',
    endDate: '2023-12-31',
    status: 'not-started'
  }, {
    name: 'Property Management Setup',
    startDate: '2023-06-15',
    endDate: '2023-07-15',
    status: 'not-started'
  }]
}];
// Mock data for property budget
const mockPropertyBudget = {
  categories: [{
    name: 'Acquisition Costs',
    original: 4500000,
    current: 4800000,
    actual: 4800000,
    variance: -300000
  }, {
    name: 'Closing Costs',
    original: 75000,
    current: 82000,
    actual: 82000,
    variance: -7000
  }, {
    name: 'Renovation Budget',
    original: 960000,
    current: 985000,
    actual: 420000,
    variance: 565000
  }, {
    name: 'Holding Costs',
    original: 120000,
    current: 120000,
    actual: 60000,
    variance: 60000
  }, {
    name: 'Marketing & Leasing',
    original: 45000,
    current: 45000,
    actual: 0,
    variance: 45000
  }, {
    name: 'Contingency',
    original: 100000,
    current: 100000,
    actual: 25000,
    variance: 75000
  }],
  summary: {
    originalTotal: 5800000,
    currentTotal: 6132000,
    actualToDate: 5387000,
    remainingBudget: 745000,
    overallVariance: -332000
  }
};
// Mock data for property change orders
const mockPropertyChangeOrders = [{
  id: 'pco1',
  number: 'PCO-001',
  description: 'Additional environmental remediation required after inspection',
  date: '2023-02-20',
  amount: 150000,
  status: 'approved'
}, {
  id: 'pco2',
  number: 'PCO-002',
  description: 'Roof replacement instead of repair due to discovered damage',
  date: '2023-03-05',
  amount: 95000,
  status: 'approved'
}, {
  id: 'pco3',
  number: 'PCO-003',
  description: 'Upgrade all common area finishes to premium materials',
  date: '2023-04-10',
  amount: 65000,
  status: 'approved'
}, {
  id: 'pco4',
  number: 'PCO-004',
  description: 'Add smart home technology package to all units',
  date: '2023-05-15',
  amount: 72000,
  status: 'pending'
}];
// Mock data for property materials
const mockPropertyMaterials = {
  categories: [{
    name: 'Common Area Finishes',
    items: [{
      name: 'Lobby Flooring',
      description: 'Porcelain tile, 24"x24", marble look',
      brand: 'Daltile',
      model: 'Grandeur Collection',
      cost: 8.5,
      unit: 'sq ft',
      quantity: 800,
      totalCost: 6800,
      status: 'ordered',
      notes: 'Delivery expected next week'
    }, {
      name: 'Hallway Carpet',
      description: 'Commercial grade carpet tiles, charcoal',
      brand: 'Interface',
      model: 'Urban Retreat Collection',
      cost: 4.25,
      unit: 'sq ft',
      quantity: 3200,
      totalCost: 13600,
      status: 'selected',
      notes: 'Need to confirm exact quantity'
    }]
  }, {
    name: 'Exterior Materials',
    items: [{
      name: 'Exterior Paint',
      description: 'Weather resistant paint, modern gray',
      brand: 'Sherwin Williams',
      model: 'Duration Exterior',
      cost: 48.0,
      unit: 'gallon',
      quantity: 120,
      totalCost: 5760,
      status: 'ordered',
      notes: 'Color: Mindful Gray'
    }, {
      name: 'Landscaping Package',
      description: 'Drought-resistant plants and irrigation system',
      brand: 'Various',
      model: 'N/A',
      cost: 15000,
      unit: 'total',
      quantity: 1,
      totalCost: 15000,
      status: 'pending',
      notes: 'Working with landscaper on final design'
    }]
  }, {
    name: 'Apartment Unit Standards',
    items: [{
      name: 'Kitchen Appliance Package',
      description: 'Stainless steel refrigerator, range, microwave, dishwasher',
      brand: 'GE',
      model: 'Profile Series',
      cost: 3200,
      unit: 'package',
      quantity: 24,
      totalCost: 76800,
      status: 'ordered',
      notes: '8 packages delivered, 16 pending'
    }, {
      name: 'Bathroom Vanities',
      description: 'Modern single sink vanity with marble top',
      brand: 'Allen + Roth',
      model: 'Moravia Collection',
      cost: 650,
      unit: 'each',
      quantity: 36,
      totalCost: 23400,
      status: 'selected',
      notes: 'One for each bathroom'
    }]
  }]
};
// New mock data for property management
const mockPropertyManagement = {
  propertyInfo: {
    address: '123 Main Street, Anytown, USA',
    parcelNumber: '123-456-789',
    purchaseDate: '2019-05-15',
    purchasePrice: 4800000,
    mortgage: {
      lender: 'First National Bank',
      accountNumber: 'MORT-12345678',
      interestRate: 3.75,
      dueDate: '1st of each month',
      paymentAmount: 18750
    },
    insurance: {
      carrier: 'Reliable Insurance Co.',
      policyNumber: 'POL-987654321',
      renewalDate: '2023-12-31',
      annualPremium: 12500,
      agent: 'Sarah Johnson',
      agentPhone: '(555) 123-4567'
    },
    hoa: {
      name: 'Riverside Community Association',
      contact: 'John Smith',
      phone: '(555) 987-6543',
      email: 'hoa@riversidecommunity.org',
      dues: 350,
      paymentFrequency: 'Monthly',
      rulesUrl: 'https://riversidecommunity.org/rules'
    },
    propertyTax: {
      assessorContact: 'County Tax Assessor',
      assessorPhone: '(555) 789-0123',
      annualAmount: 52000,
      installments: [{
        dueDate: '2023-11-01',
        amount: 26000
      }, {
        dueDate: '2024-02-01',
        amount: 26000
      }]
    },
    warranties: [{
      item: 'Roof',
      company: 'Superior Roofing',
      expirationDate: '2033-05-15',
      contactPhone: '(555) 222-3333',
      notes: '30-year warranty, transferable to new owner'
    }, {
      item: 'HVAC System',
      company: 'Climate Control Inc.',
      expirationDate: '2028-05-15',
      contactPhone: '(555) 444-5555',
      notes: '10-year parts, 5-year labor'
    }, {
      item: 'Appliances',
      company: 'GE',
      expirationDate: '2024-05-15',
      contactPhone: '(800) 555-6666',
      notes: 'Extended warranty purchased for all kitchen appliances'
    }],
    security: {
      alarmCode: '1234',
      systemProvider: 'Secure Homes',
      accountNumber: 'SEC-987654',
      loginInfo: 'Username: property123 (stored in password manager)'
    }
  },
  utilities: [{
    type: 'Electric',
    provider: 'City Power & Light',
    accountNumber: 'ELEC-12345',
    contact: '(555) 111-2222',
    billingCycle: 'Monthly, due on the 15th',
    autopay: true,
    website: 'www.citypowerlight.com'
  }, {
    type: 'Gas',
    provider: 'National Gas Company',
    accountNumber: 'GAS-67890',
    contact: '(555) 333-4444',
    billingCycle: 'Monthly, due on the 20th',
    autopay: true,
    website: 'www.nationalgasco.com'
  }, {
    type: 'Water/Sewer',
    provider: 'City Utilities',
    accountNumber: 'WAT-54321',
    contact: '(555) 555-6666',
    billingCycle: 'Quarterly, due on the 1st',
    autopay: false,
    website: 'www.cityutilities.gov'
  }, {
    type: 'Trash/Recycling',
    provider: 'Clean Streets Inc.',
    accountNumber: 'TRSH-98765',
    contact: '(555) 777-8888',
    billingCycle: 'Quarterly, due on the 15th',
    autopay: true,
    website: 'www.cleanstreets.com',
    pickupDays: 'Trash: Monday, Recycling: Thursday'
  }, {
    type: 'Internet/TV',
    provider: 'Fast Connect',
    accountNumber: 'NET-13579',
    contact: '(555) 999-0000',
    billingCycle: 'Monthly, due on the 5th',
    autopay: true,
    website: 'www.fastconnect.com',
    plan: 'Fiber 1GB + Premium TV Package'
  }, {
    type: 'Security Monitoring',
    provider: 'Secure Homes',
    accountNumber: 'SEC-987654',
    contact: '(555) 123-7890',
    billingCycle: 'Annually, due on June 1st',
    autopay: false,
    website: 'www.securehomes.com'
  }],
  services: [{
    type: 'Pool Service',
    provider: 'Crystal Clear Pools',
    contact: 'Mike Johnson',
    phone: '(555) 222-3333',
    email: 'mike@crystalclearpools.com',
    schedule: 'Weekly, every Tuesday',
    rate: 200,
    frequency: 'Weekly'
  }, {
    type: 'Landscaping',
    provider: 'Green Thumb Landscaping',
    contact: 'Lisa Garcia',
    phone: '(555) 444-5555',
    email: 'lisa@greenthumb.com',
    schedule: 'Bi-weekly, every other Wednesday',
    rate: 250,
    frequency: 'Bi-weekly'
  }, {
    type: 'Cleaning',
    provider: 'Spotless Cleaning Services',
    contact: 'David Chen',
    phone: '(555) 666-7777',
    email: 'david@spotlesscleaning.com',
    schedule: 'Monthly, first Monday',
    rate: 300,
    frequency: 'Monthly'
  }, {
    type: 'Pest Control',
    provider: 'No Bugs Allowed',
    contact: 'Robert Taylor',
    phone: '(555) 888-9999',
    email: 'robert@nobugsallowed.com',
    schedule: 'Quarterly, next visit Sept 15',
    rate: 150,
    frequency: 'Quarterly'
  }],
  vendors: [{
    type: 'General Handyman',
    name: "Bob's Fix-It",
    contact: 'Bob Smith',
    phone: '(555) 123-4567',
    email: 'bob@fixitall.com',
    notes: 'Great for small repairs, usually available on short notice'
  }, {
    type: 'HVAC',
    name: 'Cool Air Services',
    contact: 'James Wilson',
    phone: '(555) 234-5678',
    email: 'james@coolairservices.com',
    notes: 'Annual service contract, next service due in November'
  }, {
    type: 'Plumber',
    name: 'Flowing Waters Plumbing',
    contact: 'Maria Garcia',
    phone: '(555) 345-6789',
    email: 'maria@flowingwaters.com',
    notes: '24/7 emergency service available'
  }, {
    type: 'Electrician',
    name: 'Bright Spark Electric',
    contact: 'Thomas Lee',
    phone: '(555) 456-7890',
    email: 'thomas@brightspark.com',
    notes: 'Licensed for commercial and residential work'
  }, {
    type: 'Appliance Repair',
    name: 'Appliance Experts',
    contact: 'Sarah Johnson',
    phone: '(555) 567-8901',
    email: 'sarah@applianceexperts.com',
    notes: 'Specializes in high-end appliances'
  }, {
    type: 'Roofing',
    name: 'Top Notch Roofing',
    contact: 'Michael Brown',
    phone: '(555) 678-9012',
    email: 'michael@topnotchroofing.com',
    notes: 'Free inspections, 10-year workmanship warranty'
  }, {
    type: 'Window Cleaner',
    name: 'Crystal Clear Windows',
    contact: 'Jennifer White',
    phone: '(555) 789-0123',
    email: 'jennifer@crystalclearwindows.com',
    notes: 'Eco-friendly cleaning solutions'
  }, {
    type: 'Locksmith',
    name: 'Safe & Secure Locks',
    contact: 'David Miller',
    phone: '(555) 890-1234',
    email: 'david@safesecurelocks.com',
    notes: '24/7 emergency service, can rekey all locks in one visit'
  }],
  maintenanceSchedule: [{
    task: 'HVAC Filter Replacement',
    frequency: 'Every 3 months',
    lastCompleted: '2023-05-15',
    nextDue: '2023-08-15',
    assignedTo: 'Property Manager',
    notes: 'Use MERV 11 filters, stored in utility closet'
  }, {
    task: 'Roof Inspection',
    frequency: 'Annually',
    lastCompleted: '2023-04-10',
    nextDue: '2024-04-10',
    assignedTo: 'Top Notch Roofing',
    notes: 'Check for storm damage and clear debris'
  }, {
    task: 'Pool Chemical Check',
    frequency: 'Weekly',
    lastCompleted: '2023-06-27',
    nextDue: '2023-07-04',
    assignedTo: 'Crystal Clear Pools',
    notes: 'Included in regular service'
  }, {
    task: 'Sprinkler System Testing',
    frequency: 'Bi-annually',
    lastCompleted: '2023-03-15',
    nextDue: '2023-09-15',
    assignedTo: 'Green Thumb Landscaping',
    notes: 'Check for leaks and adjust heads'
  }, {
    task: 'Pest Control Treatment',
    frequency: 'Quarterly',
    lastCompleted: '2023-06-15',
    nextDue: '2023-09-15',
    assignedTo: 'No Bugs Allowed',
    notes: 'Perimeter and interior treatment'
  }, {
    task: 'Pressure Wash Exterior',
    frequency: 'Annually',
    lastCompleted: '2023-05-20',
    nextDue: '2024-05-20',
    assignedTo: "Bob's Fix-It",
    notes: 'Include driveway, walkways, and patio'
  }, {
    task: 'Water Heater Flush',
    frequency: 'Annually',
    lastCompleted: '2023-02-10',
    nextDue: '2024-02-10',
    assignedTo: 'Flowing Waters Plumbing',
    notes: 'Check anode rod and pressure relief valve'
  }, {
    task: 'Gutter Cleaning',
    frequency: 'Bi-annually',
    lastCompleted: '2023-04-05',
    nextDue: '2023-10-05',
    assignedTo: 'Top Notch Roofing',
    notes: 'Check downspouts and drainage'
  }],
  repairsLog: [{
    id: 'REP001',
    description: 'Replace kitchen faucet',
    dateStarted: '2023-05-10',
    dateCompleted: '2023-05-10',
    contractor: 'Flowing Waters Plumbing',
    contactInfo: '(555) 345-6789',
    cost: 385,
    paymentDate: '2023-05-10',
    paymentMethod: 'Credit Card',
    warranty: '5 years on parts and labor',
    notes: 'Installed Moen Arbor pulldown faucet in stainless',
    photos: ['https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80']
  }, {
    id: 'REP002',
    description: 'HVAC annual maintenance',
    dateStarted: '2023-05-15',
    dateCompleted: '2023-05-15',
    contractor: 'Cool Air Services',
    contactInfo: '(555) 234-5678',
    cost: 250,
    paymentDate: '2023-05-15',
    paymentMethod: 'Check #1234',
    warranty: '90 days on service',
    notes: 'Cleaned coils, checked refrigerant levels, replaced filters',
    photos: []
  }, {
    id: 'REP003',
    description: 'Repair roof leak in master bedroom',
    dateStarted: '2023-06-05',
    dateCompleted: '2023-06-07',
    contractor: 'Top Notch Roofing',
    contactInfo: '(555) 678-9012',
    cost: 875,
    paymentDate: '2023-06-10',
    paymentMethod: 'Bank Transfer',
    warranty: '2 years on repair',
    notes: 'Replaced damaged shingles and repaired flashing around chimney',
    photos: ['https://images.unsplash.com/photo-1632759145356-838b0c6a4739?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80']
  }, {
    id: 'REP004',
    description: 'Replace garbage disposal',
    dateStarted: '2023-06-20',
    dateCompleted: '2023-06-20',
    contractor: "Bob's Fix-It",
    contactInfo: '(555) 123-4567',
    cost: 325,
    paymentDate: '2023-06-20',
    paymentMethod: 'Cash',
    warranty: '1 year parts and labor',
    notes: 'Installed InSinkErator Badger 5, 1/2 HP model',
    photos: []
  }]
};
// Add this function near the top of the file
const getZillowUrl = address => {
  // Format address for URL (replace spaces with -, remove commas, etc.)
  const formattedAddress = address.replace(/\s+/g, '-').replace(/,/g, '').toLowerCase();
  return `https://www.zillow.com/homes/${formattedAddress}_rb/`;
};
const getRedfinUrl = address => {
  // Format address for URL (replace spaces with +, keep commas)
  const formattedAddress = address.replace(/\s+/g, '+');
  return `https://www.redfin.com/search?q=${formattedAddress}`;
};
const PropertyDetail = () => {
  const {
    id
  } = useParams();
  const property = mockProperties.find(p => p.id === id);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewChangeOrderForm, setShowNewChangeOrderForm] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [investmentStrategy, setInvestmentStrategy] = useState('');
  const [expectedHoldPeriod, setExpectedHoldPeriod] = useState('');
  const [newChangeOrder, setNewChangeOrder] = useState({
    number: `PCO-00${mockPropertyChangeOrders.length + 1}`,
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'pending'
  });
  // New state for property management
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemType, setAddItemType] = useState('');
  const [activeManagementSection, setActiveManagementSection] = useState('propertyInfo');
  // Find related projects for this property
  const relatedProjects = mockProjects.filter(project => project.propertyId === id);
  // Add mock Zillow and Redfin estimates (about 2% variance from property value)
  const zillowEstimate = property ? Math.round(property.value * 1.02) : 0;
  const redfinEstimate = property ? Math.round(property.value * 0.98) : 0;
  // Initialize strategy state when property is loaded
  useEffect(() => {
    if (property) {
      setInvestmentStrategy(property.investmentStrategy || 'Long-Term Hold');
      setExpectedHoldPeriod(property.expectedHoldPeriod || '5-7 years');
    }
  }, [property]);
  if (!property) {
    return <div className="text-center py-12" data-id="element-1694">
        <p className="text-gray-500" data-id="element-1695">Property not found.</p>
        <Link to="/properties" className="text-blue-600 hover:text-blue-800 mt-4 inline-block" data-id="element-1696">
          Back to Properties
        </Link>
      </div>;
  }
  // Get status colors for various elements
  const getPhaseStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'not-started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getMaterialStatusColor = status => {
    switch (status) {
      case 'selected':
        return 'bg-blue-100 text-blue-800';
      case 'ordered':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'installed':
        return 'bg-green-800 text-white';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getChangeOrderStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStrategyBadgeColor = strategy => {
    switch (strategy) {
      case 'Long-Term Hold':
        return 'bg-blue-100 text-blue-800';
      case 'Short-Term Flip':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Helper function to format date for display
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Helper function to check if maintenance task is overdue
  const isTaskOverdue = dueDate => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };
  // Helper function to check if maintenance task is due soon (within 7 days)
  const isTaskDueSoon = dueDate => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7;
  };
  const handleNewChangeOrderSubmit = e => {
    e.preventDefault();
    // In a real app, we would send this to the backend
    console.log('Submitted property change order:', newChangeOrder);
    setShowNewChangeOrderForm(false);
    // Reset form
    setNewChangeOrder({
      number: `PCO-00${mockPropertyChangeOrders.length + 2}`,
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      status: 'pending'
    });
  };
  const handleStrategySubmit = e => {
    e.preventDefault();
    // In a real app, we would update the backend
    console.log('Updated investment strategy:', {
      investmentStrategy,
      expectedHoldPeriod
    });
    setShowStrategyModal(false);
    // In a real app, we would refresh the property data
  };
  return <div data-id="element-1697">
      {/* Header with navigation */}
      <div className="mb-6" data-id="element-1698">
        <Link to="/properties" className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1699">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1700" />
          Back to Properties
        </Link>
      </div>

      {/* Property Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-1701">
        <div data-id="element-1702">
          <div className="flex items-center" data-id="element-1703">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1704">
              {property.name}
            </h1>
            <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStrategyBadgeColor(property.investmentStrategy || 'Long-Term Hold')}`} data-id="element-1705">
              {property.investmentStrategy || 'Long-Term Hold'}
            </span>
          </div>
          <div className="flex items-center text-gray-500 mt-1" data-id="element-1706">
            <MapPinIcon className="w-4 h-4 mr-1" data-id="element-1707" />
            <span data-id="element-1708">{property.address}</span>
          </div>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0" data-id="element-1709">
          <Button variant="outline" onClick={() => setShowStrategyModal(true)} data-id="element-1710">
            <ClockIcon className="w-4 h-4 mr-2" data-id="element-1711" />
            Set Strategy
          </Button>
          <Button variant="outline" data-id="element-1712">
            <EditIcon className="w-4 h-4 mr-2" data-id="element-1713" />
            Edit
          </Button>
          <Button variant="danger" data-id="element-1714">
            <TrashIcon className="w-4 h-4 mr-2" data-id="element-1715" />
            Delete
          </Button>
        </div>
      </div>

      {/* Property Quick Links */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6" data-id="element-1716">
        <div className="flex flex-wrap gap-3" data-id="element-1717">
          <Link to="/notes" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1718">
            <PencilIcon className="w-4 h-4 mr-2" data-id="element-1719" />
            <span data-id="element-1720">Property Logs</span>
          </Link>
          <Link to="/todos" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1721">
            <ListTodoIcon className="w-4 h-4 mr-2" data-id="element-1722" />
            <span data-id="element-1723">To-Do List</span>
          </Link>
          <Link to="/file-cabinet" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1724">
            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-1725" />
            <span data-id="element-1726">Documents</span>
          </Link>
          {/* Add Zillow and Redfin links */}
          <a href={getZillowUrl(property.address)} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-blue-50 rounded-md text-blue-700 hover:bg-blue-100" data-id="element-1727">
            <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-1728" />
            <span data-id="element-1729">View on Zillow</span>
          </a>
          <a href={getRedfinUrl(property.address)} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-red-50 rounded-md text-red-700 hover:bg-red-100" data-id="element-1730">
            <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-1731" />
            <span data-id="element-1732">View on Redfin</span>
          </a>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6" data-id="element-1733">
        <nav className="flex -mb-px space-x-8 overflow-x-auto" data-id="element-1734">
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'overview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-1735">
            Overview
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'projects' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('projects')} data-id="element-1736">
            Projects
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'schedule' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('schedule')} data-id="element-1737">
            Schedule
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'budget' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('budget')} data-id="element-1738">
            Budget
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'materials' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('materials')} data-id="element-1739">
            Materials
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'logs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('logs')} data-id="element-1740">
            Logs & Tasks
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'management' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('management')} data-id="element-1741">
            Property Management
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-1742">
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="p-6" data-id="element-1743">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1744">
              <div data-id="element-1745">
                <img src={property.imageUrl} alt={property.name} className="w-full h-64 object-cover rounded-lg" data-id="element-1746" />
                <div className="mt-6 grid grid-cols-2 gap-4" data-id="element-1747">
                  <div data-id="element-1748">
                    <p className="text-sm text-gray-500" data-id="element-1749">Type</p>
                    <p className="font-medium" data-id="element-1750">{property.type}</p>
                  </div>
                  <div data-id="element-1751">
                    <p className="text-sm text-gray-500" data-id="element-1752">Size</p>
                    <p className="font-medium" data-id="element-1753">{property.size} sq ft</p>
                  </div>
                  <div data-id="element-1754">
                    <p className="text-sm text-gray-500" data-id="element-1755">Purchase Date</p>
                    <p className="font-medium" data-id="element-1756">{property.purchaseDate}</p>
                  </div>
                  <div data-id="element-1757">
                    <p className="text-sm text-gray-500" data-id="element-1758">Value</p>
                    <p className="font-medium" data-id="element-1759">
                      ${property.value.toLocaleString()}
                    </p>
                  </div>
                </div>
                {/* Add Zillow and Redfin estimates */}
                <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1760">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1761">
                    Online Valuations
                  </h3>
                  <div className="space-y-2" data-id="element-1762">
                    <div className="flex justify-between items-center" data-id="element-1763">
                      <div className="flex items-center" data-id="element-1764">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2" data-id="element-1765"></div>
                        <span className="text-sm text-gray-600" data-id="element-1766">
                          Zillow Zestimate
                        </span>
                      </div>
                      <span className="font-medium" data-id="element-1767">
                        ${zillowEstimate.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-1768">
                      <div className="flex items-center" data-id="element-1769">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2" data-id="element-1770"></div>
                        <span className="text-sm text-gray-600" data-id="element-1771">
                          Redfin Estimate
                        </span>
                      </div>
                      <span className="font-medium" data-id="element-1772">
                        ${redfinEstimate.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2" data-id="element-1773">
                      <div className="flex items-center" data-id="element-1774">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-1775"></div>
                        <span className="text-sm font-medium text-gray-700" data-id="element-1776">
                          Our Valuation
                        </span>
                      </div>
                      <span className="font-medium" data-id="element-1777">
                        ${property.value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-1778">
                <h3 className="text-lg font-medium mb-4" data-id="element-1779">Description</h3>
                <p className="text-gray-700 mb-6" data-id="element-1780">{property.description}</p>
                <h3 className="text-lg font-medium mb-4" data-id="element-1781">Details</h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1782">
                  <div data-id="element-1783">
                    <p className="text-sm text-gray-500" data-id="element-1784">Year Built</p>
                    <p className="font-medium" data-id="element-1785">{property.yearBuilt}</p>
                  </div>
                  <div data-id="element-1786">
                    <p className="text-sm text-gray-500" data-id="element-1787">Condition</p>
                    <p className="font-medium" data-id="element-1788">{property.condition}</p>
                  </div>
                  <div data-id="element-1789">
                    <p className="text-sm text-gray-500" data-id="element-1790">Current Use</p>
                    <p className="font-medium" data-id="element-1791">{property.currentUse}</p>
                  </div>
                  <div data-id="element-1792">
                    <p className="text-sm text-gray-500" data-id="element-1793">Occupancy</p>
                    <p className="font-medium" data-id="element-1794">{property.occupancy}%</p>
                  </div>
                </div>
                {/* Investment Strategy Section */}
                <div className="mt-6" data-id="element-1795">
                  <h3 className="text-lg font-medium mb-4" data-id="element-1796">
                    Investment Strategy
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1797">
                    <div className="flex justify-between items-center mb-4" data-id="element-1798">
                      <div className="flex items-center" data-id="element-1799">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStrategyBadgeColor(property.investmentStrategy || 'Long-Term Hold')}`} data-id="element-1800">
                          {property.investmentStrategy || 'Long-Term Hold'}
                        </span>
                        <span className="ml-3 text-sm text-gray-500" data-id="element-1801">
                          Expected Hold:{' '}
                          {property.expectedHoldPeriod || '5-7 years'}
                        </span>
                      </div>
                      <button onClick={() => setShowStrategyModal(true)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1802">
                        Change
                      </button>
                    </div>
                    {property.investmentStrategy === 'Long-Term Hold' ? <div className="grid grid-cols-2 gap-4" data-id="element-1803">
                        <div data-id="element-1804">
                          <p className="text-sm text-gray-500" data-id="element-1805">
                            Annual Cash Flow
                          </p>
                          <p className="font-medium" data-id="element-1806">
                            $
                            {property.annualCashFlow?.toLocaleString() || 'N/A'}
                          </p>
                        </div>
                        <div data-id="element-1807">
                          <p className="text-sm text-gray-500" data-id="element-1808">Cap Rate</p>
                          <p className="font-medium" data-id="element-1809">
                            {property.capRate || 'N/A'}%
                          </p>
                        </div>
                      </div> : <div className="grid grid-cols-2 gap-4" data-id="element-1810">
                        <div data-id="element-1811">
                          <p className="text-sm text-gray-500" data-id="element-1812">
                            Projected Profit
                          </p>
                          <p className="font-medium" data-id="element-1813">
                            $
                            {property.projectedProfit?.toLocaleString() || 'N/A'}
                          </p>
                        </div>
                        <div data-id="element-1814">
                          <p className="text-sm text-gray-500" data-id="element-1815">Projected ROI</p>
                          <p className="font-medium" data-id="element-1816">
                            {property.projectedROI || 'N/A'}%
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>
                <div className="mt-6" data-id="element-1817">
                  <h3 className="text-lg font-medium mb-4" data-id="element-1818">
                    Financial Summary
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1819">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1820">
                      <div data-id="element-1821">
                        <p className="text-sm text-gray-500" data-id="element-1822">Purchase Price</p>
                        <p className="font-medium" data-id="element-1823">
                          $
                          {mockPropertyBudget.categories[0].actual.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1824">
                        <p className="text-sm text-gray-500" data-id="element-1825">Current Value</p>
                        <p className="font-medium" data-id="element-1826">
                          ${property.value.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1827">
                        <p className="text-sm text-gray-500" data-id="element-1828">Total Invested</p>
                        <p className="font-medium" data-id="element-1829">
                          $
                          {mockPropertyBudget.summary.actualToDate.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1830">
                        <p className="text-sm text-gray-500" data-id="element-1831">Equity</p>
                        <p className="font-medium" data-id="element-1832">
                          $
                          {(property.value - mockPropertyBudget.summary.actualToDate).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Projects Tab */}
        {activeTab === 'projects' && <div className="p-6" data-id="element-1833">
            <div className="flex justify-between items-center mb-4" data-id="element-1834">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1835">
                Construction Projects
              </h2>
              <Button data-id="element-1836">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-1837" />
                Add Project
              </Button>
            </div>
            {relatedProjects.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1838">
                {relatedProjects.map(project => <ProjectCard key={project.id} project={project} data-id="element-1839" />)}
              </div> : <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center" data-id="element-1840">
                <ConstructionIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-1841" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-1842">
                  No Projects Yet
                </h3>
                <p className="text-gray-500 mb-4" data-id="element-1843">
                  This property doesn't have any construction projects.
                </p>
                <Button data-id="element-1844">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1845" />
                  Start New Project
                </Button>
              </div>}
          </div>}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && <div className="p-6" data-id="element-1846">
            <div className="flex justify-between items-center mb-6" data-id="element-1847">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1848">
                Property Timeline
              </h3>
              <Button size="sm" data-id="element-1849">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-1850" />
                Add Phase
              </Button>
            </div>
            <div className="space-y-6" data-id="element-1851">
              {mockPropertySchedule.map(phase => <div key={phase.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1852">
                  <div className="bg-gray-50 p-4 flex justify-between items-center" data-id="element-1853">
                    <div className="flex items-center" data-id="element-1854">
                      <h4 className="font-medium text-gray-900" data-id="element-1855">
                        {phase.name}
                      </h4>
                      <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(phase.status)}`} data-id="element-1856">
                        {phase.status.charAt(0).toUpperCase() + phase.status.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1857">
                      {phase.startDate} to {phase.endDate}
                    </div>
                  </div>
                  <div className="p-4" data-id="element-1858">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1859">
                      <thead data-id="element-1860">
                        <tr data-id="element-1861">
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1862">
                            Task
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1863">
                            Start Date
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1864">
                            End Date
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1865">
                            Status
                          </th>
                          <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1866">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1867">
                        {phase.tasks.map((task, index) => <tr key={index} data-id="element-1868">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1869">
                              {task.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1870">
                              {task.startDate}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1871">
                              {task.endDate}
                            </td>
                            <td className="px-4 py-3" data-id="element-1872">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(task.status)}`} data-id="element-1873">
                                {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1874">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1875">
                                Edit
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    <div className="mt-3 flex justify-end" data-id="element-1876">
                      <Button size="sm" variant="outline" data-id="element-1877">
                        <PlusIcon className="w-3 h-3 mr-1" data-id="element-1878" />
                        Add Task
                      </Button>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="mt-8" data-id="element-1879">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1880">
                Timeline Overview
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1881">
                <div className="relative" data-id="element-1882">
                  {/* Timeline header - months */}
                  <div className="flex border-b border-gray-300 pb-2" data-id="element-1883">
                    <div className="w-1/6" data-id="element-1884"></div>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => <div key={index} className="flex-1 text-center text-xs font-medium text-gray-500" data-id="element-1885">
                        {month}
                      </div>)}
                  </div>
                  {/* Timeline for each phase */}
                  {mockPropertySchedule.map((phase, phaseIndex) => <div key={phase.id} className={`flex items-center py-3 ${phaseIndex !== mockPropertySchedule.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-1886">
                      <div className="w-1/6 pr-4" data-id="element-1887">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1888">
                          {phase.name}
                        </p>
                      </div>
                      <div className="flex-1 relative h-6" data-id="element-1889">
                        {/* Calculate position and width based on dates */}
                        <div className={`absolute h-full rounded-full ${phase.status === 'completed' ? 'bg-green-500' : phase.status === 'in-progress' ? 'bg-blue-500' : 'bg-gray-300'}`} style={{
                    left: phaseIndex === 0 ? '0%' : phaseIndex === 1 ? '12%' : phaseIndex === 2 ? '20%' : '45%',
                    width: phaseIndex === 0 ? '12%' : phaseIndex === 1 ? '8%' : phaseIndex === 2 ? '25%' : '30%'
                  }} data-id="element-1890"></div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>}

        {/* Budget Tab */}
        {activeTab === 'budget' && <div className="p-6" data-id="element-1891">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-1892">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1893">
                Property Budget
              </h3>
              <div className="mt-3 md:mt-0" data-id="element-1894">
                <Button size="sm" data-id="element-1895">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1896" />
                  Add Budget Item
                </Button>
              </div>
            </div>
            {/* Budget Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-1897">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1898">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1899">Original Budget</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1900">
                  ${mockPropertyBudget.summary.originalTotal.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1901">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1902">Current Budget</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1903">
                  ${mockPropertyBudget.summary.currentTotal.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1904">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1905">Actual to Date</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1906">
                  ${mockPropertyBudget.summary.actualToDate.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1907">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1908">Remaining</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1909">
                  ${mockPropertyBudget.summary.remainingBudget.toLocaleString()}
                </p>
              </div>
            </div>
            {/* Budget Categories */}
            <div className="mb-8" data-id="element-1910">
              <h4 className="text-md font-medium text-gray-900 mb-3" data-id="element-1911">
                Budget Categories
              </h4>
              <div className="overflow-x-auto" data-id="element-1912">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1913">
                  <thead data-id="element-1914">
                    <tr data-id="element-1915">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1916">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1917">
                        Original Budget
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1918">
                        Current Budget
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1919">
                        Actual Cost
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1920">
                        Variance
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1921">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1922">
                    {mockPropertyBudget.categories.map((category, index) => <tr key={index} data-id="element-1923">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1924">
                          {category.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1925">
                          ${category.original.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1926">
                          ${category.current.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1927">
                          ${category.actual.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium text-right ${category.variance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1928">
                          {category.variance >= 0 ? '+' : ''}
                          {category.variance.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1929">
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-1930">
                            Edit
                          </button>
                        </td>
                      </tr>)}
                    <tr className="bg-gray-50" data-id="element-1931">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1932">
                        Total
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1933">
                        $
                        {mockPropertyBudget.summary.originalTotal.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1934">
                        $
                        {mockPropertyBudget.summary.currentTotal.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1935">
                        $
                        {mockPropertyBudget.summary.actualToDate.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium text-right ${mockPropertyBudget.summary.overallVariance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1936">
                        {mockPropertyBudget.summary.overallVariance >= 0 ? '+' : ''}
                        {mockPropertyBudget.summary.overallVariance.toLocaleString()}
                      </td>
                      <td className="px-4 py-3" data-id="element-1937"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {/* Change Orders */}
            <div data-id="element-1938">
              <div className="flex justify-between items-center mb-4" data-id="element-1939">
                <h4 className="text-md font-medium text-gray-900" data-id="element-1940">
                  Change Orders
                </h4>
                <Button size="sm" onClick={() => setShowNewChangeOrderForm(!showNewChangeOrderForm)} data-id="element-1941">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1942" />
                  New Change Order
                </Button>
              </div>
              {/* New Change Order Form */}
              {showNewChangeOrderForm && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" data-id="element-1943">
                  <h5 className="font-medium text-gray-900 mb-3" data-id="element-1944">
                    New Change Order
                  </h5>
                  <form onSubmit={handleNewChangeOrderSubmit} data-id="element-1945">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1946">
                      <div data-id="element-1947">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1948">
                          Change Order #
                        </label>
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.number} readOnly data-id="element-1949" />
                      </div>
                      <div data-id="element-1950">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1951">
                          Date
                        </label>
                        <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.date} onChange={e => setNewChangeOrder({
                    ...newChangeOrder,
                    date: e.target.value
                  })} data-id="element-1952" />
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-1953">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1954">
                        Description
                      </label>
                      <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" rows="3" value={newChangeOrder.description} onChange={e => setNewChangeOrder({
                  ...newChangeOrder,
                  description: e.target.value
                })} placeholder="Describe the change order..." data-id="element-1955"></textarea>
                    </div>
                    <div className="mb-4" data-id="element-1956">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1957">
                        Amount ($)
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.amount} onChange={e => setNewChangeOrder({
                  ...newChangeOrder,
                  amount: e.target.value
                })} placeholder="0.00" data-id="element-1958" />
                    </div>
                    <div className="flex justify-end space-x-3" data-id="element-1959">
                      <Button variant="outline" type="button" onClick={() => setShowNewChangeOrderForm(false)} data-id="element-1960">
                        Cancel
                      </Button>
                      <Button type="submit" data-id="element-1961">Submit Change Order</Button>
                    </div>
                  </form>
                </div>}
              <div className="overflow-x-auto" data-id="element-1962">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1963">
                  <thead data-id="element-1964">
                    <tr data-id="element-1965">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1966">
                        CO #
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1967">
                        Date
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1968">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1969">
                        Amount
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1970">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1971">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1972">
                    {mockPropertyChangeOrders.map(co => <tr key={co.id} data-id="element-1973">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1974">
                          {co.number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1975">
                          {co.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1976">
                          {co.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1977">
                          ${co.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1978">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChangeOrderStatusColor(co.status)}`} data-id="element-1979">
                            {co.status.charAt(0).toUpperCase() + co.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1980">
                          <button className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-1981">
                            Edit
                          </button>
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-1982">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}

        {/* Materials Tab */}
        {activeTab === 'materials' && <div className="p-6" data-id="element-1983">
            <div className="flex justify-between items-center mb-6" data-id="element-1984">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1985">
                Property Materials
              </h3>
              <div data-id="element-1986">
                <Button size="sm" data-id="element-1987">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1988" />
                  Add Material
                </Button>
              </div>
            </div>
            {/* Material Categories */}
            <div className="space-y-8" data-id="element-1989">
              {mockPropertyMaterials.categories.map(category => <div key={category.name} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1990">
                  <div className="bg-gray-50 p-4" data-id="element-1991">
                    <h4 className="font-medium text-gray-900" data-id="element-1992">
                      {category.name}
                    </h4>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1993">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1994">
                      <thead data-id="element-1995">
                        <tr data-id="element-1996">
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1997">
                            Item
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1998">
                            Description
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1999">
                            Brand/Model
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2000">
                            Unit Cost
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2001">
                            Quantity
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2002">
                            Total
                          </th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2003">
                            Status
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2004">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-2005">
                        {category.items.map((item, index) => <tr key={index} data-id="element-2006">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2007">
                              {item.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2008">
                              {item.description}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2009">
                              {item.brand} {item.model}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-2010">
                              ${item.cost.toFixed(2)}/{item.unit}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-2011">
                              {item.quantity} {item.unit}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-2012">
                              ${item.totalCost.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2013">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMaterialStatusColor(item.status)}`} data-id="element-2014">
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-2015">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-2016">
                                Edit
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>)}
            </div>
            {/* Specifications */}
            <div className="mt-8" data-id="element-2017">
              <div className="flex justify-between items-center mb-4" data-id="element-2018">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2019">
                  Property Specifications
                </h3>
                <Button size="sm" data-id="element-2020">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-2021" />
                  Add Specification
                </Button>
              </div>
              <div className="space-y-4" data-id="element-2022">
                {mockPropertySpecifications.map(spec => <div key={spec.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50" data-id="element-2023">
                    <div className="flex justify-between items-start" data-id="element-2024">
                      <div data-id="element-2025">
                        <h4 className="font-medium text-gray-900" data-id="element-2026">
                          {spec.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-2027">
                          {spec.content}
                        </p>
                      </div>
                      <div className="flex items-center" data-id="element-2028">
                        <span className="text-xs text-gray-500 mr-3" data-id="element-2029">
                          Last updated: {spec.lastUpdated}
                        </span>
                        <button className="text-blue-600 hover:text-blue-900 text-sm font-medium" data-id="element-2030">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div className="mt-2" data-id="element-2031">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2032">
                        {spec.category}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}

        {/* Logs & Tasks Tab */}
        {activeTab === 'logs' && <div className="p-6" data-id="element-2033">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2034">
              {/* Daily Logs Section */}
              <div data-id="element-2035">
                <div className="flex justify-between items-center mb-4" data-id="element-2036">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2037">
                    Property Logs
                  </h3>
                  <Link to="/notes" className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" data-id="element-2038">
                    View All Logs
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-2039" />
                  </Link>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-2040">
                  <div className="border-b border-gray-200" data-id="element-2041">
                    <div className="p-4 flex items-center justify-between" data-id="element-2042">
                      <div className="flex items-center" data-id="element-2043">
                        <div className="p-1.5 rounded-full bg-blue-100" data-id="element-2044">
                          <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-2045" />
                        </div>
                        <div className="ml-3" data-id="element-2046">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-2047">
                            Tenant Move-In Inspection
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-2048">
                            Today at 9:15 AM
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2049">
                        Completed
                      </span>
                    </div>
                    <div className="px-4 pb-4" data-id="element-2050">
                      <p className="text-sm text-gray-600" data-id="element-2051">
                        Completed move-in inspection for Unit 12B. All items in
                        good condition. Tenant signed inspection report.
                      </p>
                    </div>
                  </div>
                  <div className="border-b border-gray-200" data-id="element-2052">
                    <div className="p-4 flex items-center justify-between" data-id="element-2053">
                      <div className="flex items-center" data-id="element-2054">
                        <div className="p-1.5 rounded-full bg-purple-100" data-id="element-2055">
                          <FileTextIcon className="w-4 h-4 text-purple-600" data-id="element-2056" />
                        </div>
                        <div className="ml-3" data-id="element-2057">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-2058">
                            Property Tax Assessment
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-2059">
                            Yesterday at 2:30 PM
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-4" data-id="element-2060">
                      <p className="text-sm text-gray-600" data-id="element-2061">
                        Received new property tax assessment. Value increased by
                        8% from previous year. Need to review for possible
                        appeal.
                      </p>
                    </div>
                  </div>
                  <div data-id="element-2062">
                    <div className="p-4 flex items-center justify-between" data-id="element-2063">
                      <div className="flex items-center" data-id="element-2064">
                        <div className="p-1.5 rounded-full bg-yellow-100" data-id="element-2065">
                          <AlertCircleIcon className="w-4 h-4 text-yellow-600" data-id="element-2066" />
                        </div>
                        <div className="ml-3" data-id="element-2067">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-2068">
                            Maintenance Request
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-2069">
                            May 10, 2023 at 11:20 AM
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2070">
                        In Progress
                      </span>
                    </div>
                    <div className="px-4 pb-4" data-id="element-2071">
                      <p className="text-sm text-gray-600" data-id="element-2072">
                        Unit 8A reported water leak under kitchen sink.
                        Maintenance team scheduled for repair tomorrow morning.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center" data-id="element-2073">
                  <Link to="/notes" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2074">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-2075" />
                    Create New Log Entry
                  </Link>
                </div>
              </div>
              {/* Tasks Section */}
              <div data-id="element-2076">
                <div className="flex justify-between items-center mb-4" data-id="element-2077">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2078">
                    Property Tasks
                  </h3>
                  <Link to="/todos" className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" data-id="element-2079">
                    View All Tasks
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-2080" />
                  </Link>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200" data-id="element-2081">
                  <div className="p-4 flex items-start" data-id="element-2082">
                    <div className="mr-3 mt-1" data-id="element-2083">
                      <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center" data-id="element-2084">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-2085"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-2086">
                      <div className="flex justify-between" data-id="element-2087">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2088">
                          Schedule annual HVAC maintenance
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2089">
                          High Priority
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2090">
                        Due: May 30, 2023 • Assigned to: Mark Wilson
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex items-start" data-id="element-2091">
                    <div className="mr-3 mt-1" data-id="element-2092">
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-2093"></div>
                    </div>
                    <div className="flex-1" data-id="element-2094">
                      <div className="flex justify-between" data-id="element-2095">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2096">
                          Renew property insurance policy
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2097">
                          Medium Priority
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2098">
                        Due: June 15, 2023 • Assigned to: Jessica Parker
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex items-start" data-id="element-2099">
                    <div className="mr-3 mt-1" data-id="element-2100">
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-2101">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-2102" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-2103">
                      <div className="flex justify-between" data-id="element-2104">
                        <h4 className="text-sm font-medium text-gray-500 line-through" data-id="element-2105">
                          Update tenant contact information
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2106">
                          Completed
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2107">
                        Completed: May 5, 2023 • Assigned to: Alex Thompson
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex items-start" data-id="element-2108">
                    <div className="mr-3 mt-1" data-id="element-2109">
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-2110"></div>
                    </div>
                    <div className="flex-1" data-id="element-2111">
                      <div className="flex justify-between" data-id="element-2112">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2113">
                          Quarterly financial review
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2114">
                          Medium Priority
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2115">
                        Due: June 30, 2023 • Assigned to: David Roberts
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center" data-id="element-2116">
                  <Link to="/todos" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2117">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-2118" />
                    Create New Task
                  </Link>
                </div>
              </div>
            </div>
          </div>}

        {/* New Property Management Tab */}
        {activeTab === 'management' && <div className="p-6" data-id="element-2119">
            <div className="flex justify-between items-center mb-6" data-id="element-2120">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2121">
                Property Management
              </h2>
              <Button onClick={() => {
            setAddItemType('');
            setShowAddItemModal(true);
          }} data-id="element-2122">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-2123" />
                Add Item
              </Button>
            </div>
            {/* Property Management Navigation */}
            <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4" data-id="element-2124">
              <button className={`px-3 py-2 rounded-md text-sm font-medium ${activeManagementSection === 'propertyInfo' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveManagementSection('propertyInfo')} data-id="element-2125">
                <BuildingIcon className="w-4 h-4 inline mr-1" data-id="element-2126" />
                Property Info
              </button>
              <button className={`px-3 py-2 rounded-md text-sm font-medium ${activeManagementSection === 'utilities' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveManagementSection('utilities')} data-id="element-2127">
                <ZapIcon className="w-4 h-4 inline mr-1" data-id="element-2128" />
                Utilities & Services
              </button>
              <button className={`px-3 py-2 rounded-md text-sm font-medium ${activeManagementSection === 'vendors' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveManagementSection('vendors')} data-id="element-2129">
                <div className="w-4 h-4 inline mr-1" data-id="element-2130" />
                Vendors & Contacts
              </button>
              <button className={`px-3 py-2 rounded-md text-sm font-medium ${activeManagementSection === 'maintenance' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveManagementSection('maintenance')} data-id="element-2131">
                <CalendarDaysIcon className="w-4 h-4 inline mr-1" data-id="element-2132" />
                Maintenance Schedule
              </button>
              <button className={`px-3 py-2 rounded-md text-sm font-medium ${activeManagementSection === 'repairs' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveManagementSection('repairs')} data-id="element-2133">
                <WrenchIcon className="w-4 h-4 inline mr-1" data-id="element-2134" />
                Repairs & Projects
              </button>
            </div>
            {/* Property Info Section */}
            {activeManagementSection === 'propertyInfo' && <div data-id="element-2135">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2136">
                  {/* Basic Property Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2137">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2138">
                      <BuildingIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2139" />
                      Basic Information
                    </h3>
                    <div className="space-y-3" data-id="element-2140">
                      <div data-id="element-2141">
                        <p className="text-sm text-gray-500" data-id="element-2142">Address</p>
                        <p className="font-medium" data-id="element-2143">
                          {mockPropertyManagement.propertyInfo.address}
                        </p>
                      </div>
                      <div data-id="element-2144">
                        <p className="text-sm text-gray-500" data-id="element-2145">Parcel Number</p>
                        <p className="font-medium" data-id="element-2146">
                          {mockPropertyManagement.propertyInfo.parcelNumber}
                        </p>
                      </div>
                      <div data-id="element-2147">
                        <p className="text-sm text-gray-500" data-id="element-2148">Purchase Date</p>
                        <p className="font-medium" data-id="element-2149">
                          {formatDate(mockPropertyManagement.propertyInfo.purchaseDate)}
                        </p>
                      </div>
                      <div data-id="element-2150">
                        <p className="text-sm text-gray-500" data-id="element-2151">Purchase Price</p>
                        <p className="font-medium" data-id="element-2152">
                          $
                          {mockPropertyManagement.propertyInfo.purchasePrice.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2153">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2154">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2155" />
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* Mortgage Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2156">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2157">
                      <DollarSignIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2158" />
                      Mortgage Details
                    </h3>
                    <div className="space-y-3" data-id="element-2159">
                      <div data-id="element-2160">
                        <p className="text-sm text-gray-500" data-id="element-2161">Lender</p>
                        <p className="font-medium" data-id="element-2162">
                          {mockPropertyManagement.propertyInfo.mortgage.lender}
                        </p>
                      </div>
                      <div data-id="element-2163">
                        <p className="text-sm text-gray-500" data-id="element-2164">Account Number</p>
                        <p className="font-medium" data-id="element-2165">
                          {mockPropertyManagement.propertyInfo.mortgage.accountNumber}
                        </p>
                      </div>
                      <div data-id="element-2166">
                        <p className="text-sm text-gray-500" data-id="element-2167">Interest Rate</p>
                        <p className="font-medium" data-id="element-2168">
                          {mockPropertyManagement.propertyInfo.mortgage.interestRate}
                          %
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2169">
                        <div data-id="element-2170">
                          <p className="text-sm text-gray-500" data-id="element-2171">Payment Due</p>
                          <p className="font-medium" data-id="element-2172">
                            {mockPropertyManagement.propertyInfo.mortgage.dueDate}
                          </p>
                        </div>
                        <div data-id="element-2173">
                          <p className="text-sm text-gray-500" data-id="element-2174">
                            Payment Amount
                          </p>
                          <p className="font-medium" data-id="element-2175">
                            $
                            {mockPropertyManagement.propertyInfo.mortgage.paymentAmount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2176">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2177">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2178" />
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* Insurance Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2179">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2180">
                      <ShieldIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2181" />
                      Property Insurance
                    </h3>
                    <div className="space-y-3" data-id="element-2182">
                      <div data-id="element-2183">
                        <p className="text-sm text-gray-500" data-id="element-2184">
                          Insurance Carrier
                        </p>
                        <p className="font-medium" data-id="element-2185">
                          {mockPropertyManagement.propertyInfo.insurance.carrier}
                        </p>
                      </div>
                      <div data-id="element-2186">
                        <p className="text-sm text-gray-500" data-id="element-2187">Policy Number</p>
                        <p className="font-medium" data-id="element-2188">
                          {mockPropertyManagement.propertyInfo.insurance.policyNumber}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2189">
                        <div data-id="element-2190">
                          <p className="text-sm text-gray-500" data-id="element-2191">Renewal Date</p>
                          <p className="font-medium" data-id="element-2192">
                            {formatDate(mockPropertyManagement.propertyInfo.insurance.renewalDate)}
                          </p>
                        </div>
                        <div data-id="element-2193">
                          <p className="text-sm text-gray-500" data-id="element-2194">
                            Annual Premium
                          </p>
                          <p className="font-medium" data-id="element-2195">
                            $
                            {mockPropertyManagement.propertyInfo.insurance.annualPremium.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2196">
                        <div data-id="element-2197">
                          <p className="text-sm text-gray-500" data-id="element-2198">Agent</p>
                          <p className="font-medium" data-id="element-2199">
                            {mockPropertyManagement.propertyInfo.insurance.agent}
                          </p>
                        </div>
                        <div data-id="element-2200">
                          <p className="text-sm text-gray-500" data-id="element-2201">Agent Phone</p>
                          <p className="font-medium" data-id="element-2202">
                            {mockPropertyManagement.propertyInfo.insurance.agentPhone}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2203">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2204">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2205" />
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* HOA Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2206">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2207">
                      <UserIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2208" />
                      HOA Information
                    </h3>
                    <div className="space-y-3" data-id="element-2209">
                      <div data-id="element-2210">
                        <p className="text-sm text-gray-500" data-id="element-2211">HOA Name</p>
                        <p className="font-medium" data-id="element-2212">
                          {mockPropertyManagement.propertyInfo.hoa.name}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2213">
                        <div data-id="element-2214">
                          <p className="text-sm text-gray-500" data-id="element-2215">Contact</p>
                          <p className="font-medium" data-id="element-2216">
                            {mockPropertyManagement.propertyInfo.hoa.contact}
                          </p>
                        </div>
                        <div data-id="element-2217">
                          <p className="text-sm text-gray-500" data-id="element-2218">Phone</p>
                          <p className="font-medium" data-id="element-2219">
                            {mockPropertyManagement.propertyInfo.hoa.phone}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-2220">
                        <p className="text-sm text-gray-500" data-id="element-2221">Email</p>
                        <p className="font-medium" data-id="element-2222">
                          {mockPropertyManagement.propertyInfo.hoa.email}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2223">
                        <div data-id="element-2224">
                          <p className="text-sm text-gray-500" data-id="element-2225">Dues</p>
                          <p className="font-medium" data-id="element-2226">
                            ${mockPropertyManagement.propertyInfo.hoa.dues}
                            /month
                          </p>
                        </div>
                        <div data-id="element-2227">
                          <p className="text-sm text-gray-500" data-id="element-2228">
                            Payment Frequency
                          </p>
                          <p className="font-medium" data-id="element-2229">
                            {mockPropertyManagement.propertyInfo.hoa.paymentFrequency}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-2230">
                        <p className="text-sm text-gray-500" data-id="element-2231">HOA Rules</p>
                        <a href={mockPropertyManagement.propertyInfo.hoa.rulesUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-2232">
                          View Rules
                          <ExternalLinkIcon className="w-3 h-3 inline ml-1" data-id="element-2233" />
                        </a>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2234">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2235">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2236" />
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* Property Tax Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2237">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2238">
                      <ReceiptIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2239" />
                      Property Tax Information
                    </h3>
                    <div className="space-y-3" data-id="element-2240">
                      <div className="grid grid-cols-2 gap-4" data-id="element-2241">
                        <div data-id="element-2242">
                          <p className="text-sm text-gray-500" data-id="element-2243">
                            Assessor Contact
                          </p>
                          <p className="font-medium" data-id="element-2244">
                            {mockPropertyManagement.propertyInfo.propertyTax.assessorContact}
                          </p>
                        </div>
                        <div data-id="element-2245">
                          <p className="text-sm text-gray-500" data-id="element-2246">Phone</p>
                          <p className="font-medium" data-id="element-2247">
                            {mockPropertyManagement.propertyInfo.propertyTax.assessorPhone}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-2248">
                        <p className="text-sm text-gray-500" data-id="element-2249">
                          Annual Tax Amount
                        </p>
                        <p className="font-medium" data-id="element-2250">
                          $
                          {mockPropertyManagement.propertyInfo.propertyTax.annualAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-2251">
                        <p className="text-sm text-gray-500" data-id="element-2252">
                          Tax Installments
                        </p>
                        <div className="mt-2 space-y-2" data-id="element-2253">
                          {mockPropertyManagement.propertyInfo.propertyTax.installments.map((installment, index) => <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded" data-id="element-2254">
                                <span className="text-sm" data-id="element-2255">
                                  Due: {formatDate(installment.dueDate)}
                                </span>
                                <span className="font-medium" data-id="element-2256">
                                  ${installment.amount.toLocaleString()}
                                </span>
                              </div>)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2257">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2258">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2259" />
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* Warranty Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2260">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2261">
                      <ClipboardIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2262" />
                      Warranty Information
                    </h3>
                    <div className="space-y-3" data-id="element-2263">
                      {mockPropertyManagement.propertyInfo.warranties.map((warranty, index) => <div key={index} className="border border-gray-200 rounded p-3" data-id="element-2264">
                            <div className="flex justify-between" data-id="element-2265">
                              <p className="font-medium" data-id="element-2266">{warranty.item}</p>
                              <p className="text-sm text-gray-500" data-id="element-2267">
                                Expires: {formatDate(warranty.expirationDate)}
                              </p>
                            </div>
                            <div className="mt-2 text-sm" data-id="element-2268">
                              <p data-id="element-2269">
                                <span className="text-gray-500" data-id="element-2270">Company:</span>{' '}
                                {warranty.company}
                              </p>
                              <p data-id="element-2271">
                                <span className="text-gray-500" data-id="element-2272">Contact:</span>{' '}
                                {warranty.contactPhone}
                              </p>
                              <p className="text-gray-600 mt-1" data-id="element-2273">
                                {warranty.notes}
                              </p>
                            </div>
                          </div>)}
                    </div>
                    <div className="mt-3 text-right" data-id="element-2274">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2275">
                        <PlusCircleIcon className="w-3 h-3 inline mr-1" data-id="element-2276" />
                        Add Warranty
                      </button>
                    </div>
                  </div>
                  {/* Security Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2277">
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-2278">
                      <KeyIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-2279" />
                      Security Information
                    </h3>
                    <div className="space-y-3" data-id="element-2280">
                      <div data-id="element-2281">
                        <p className="text-sm text-gray-500" data-id="element-2282">Alarm Code</p>
                        <p className="font-medium" data-id="element-2283">
                          {mockPropertyManagement.propertyInfo.security.alarmCode}
                        </p>
                      </div>
                      <div data-id="element-2284">
                        <p className="text-sm text-gray-500" data-id="element-2285">System Provider</p>
                        <p className="font-medium" data-id="element-2286">
                          {mockPropertyManagement.propertyInfo.security.systemProvider}
                        </p>
                      </div>
                      <div data-id="element-2287">
                        <p className="text-sm text-gray-500" data-id="element-2288">Account Number</p>
                        <p className="font-medium" data-id="element-2289">
                          {mockPropertyManagement.propertyInfo.security.accountNumber}
                        </p>
                      </div>
                      <div data-id="element-2290">
                        <p className="text-sm text-gray-500" data-id="element-2291">
                          Login Information
                        </p>
                        <p className="font-medium" data-id="element-2292">
                          {mockPropertyManagement.propertyInfo.security.loginInfo}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-right" data-id="element-2293">
                      <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2294">
                        <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2295" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            {/* Utilities & Services Section */}
            {activeManagementSection === 'utilities' && <div data-id="element-2296">
                <div className="mb-6" data-id="element-2297">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-2298">
                    <ZapIcon className="w-5 h-5 mr-2 text-gray-500" data-id="element-2299" />
                    Utilities
                  </h3>
                  <div className="overflow-x-auto" data-id="element-2300">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-2301">
                      <thead data-id="element-2302">
                        <tr data-id="element-2303">
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2304">
                            Type
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2305">
                            Provider
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2306">
                            Account #
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2307">
                            Billing Cycle
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2308">
                            Auto-Pay
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2309">
                            Contact
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2310">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2311">
                        {mockPropertyManagement.utilities.map((utility, index) => <tr key={index} data-id="element-2312">
                              <td className="px-4 py-4 whitespace-nowrap" data-id="element-2313">
                                <div className="flex items-center" data-id="element-2314">
                                  {utility.type === 'Electric' && <ZapIcon className="w-4 h-4 text-yellow-500 mr-2" data-id="element-2315" />}
                                  {utility.type === 'Gas' && <ActivityIcon className="w-4 h-4 text-blue-500 mr-2" data-id="element-2316" />}
                                  {utility.type === 'Water/Sewer' && <DropletIcon className="w-4 h-4 text-blue-400 mr-2" data-id="element-2317" />}
                                  {utility.type === 'Trash/Recycling' && <TrashCanIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-2318" />}
                                  {utility.type === 'Internet/TV' && <WifiIcon className="w-4 h-4 text-purple-500 mr-2" data-id="element-2319" />}
                                  {utility.type === 'Security Monitoring' && <ShieldIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-2320" />}
                                  <span className="font-medium text-gray-900" data-id="element-2321">
                                    {utility.type}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2322">
                                {utility.provider}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2323">
                                {utility.accountNumber}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2324">
                                {utility.billingCycle}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2325">
                                {utility.autopay ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2326">
                                    Yes
                                  </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2327">
                                    No
                                  </span>}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2328">
                                {utility.contact}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2329">
                                <button className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2330">
                                  Edit
                                </button>
                                <a href={`https://${utility.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900" data-id="element-2331">
                                  <ExternalLinkIcon className="w-4 h-4 inline" data-id="element-2332" />
                                </a>
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 text-right" data-id="element-2333">
                    <Button size="sm" onClick={() => {
                setAddItemType('utility');
                setShowAddItemModal(true);
              }} data-id="element-2334">
                      <PlusIcon className="w-4 h-4 mr-2" data-id="element-2335" />
                      Add Utility
                    </Button>
                  </div>
                </div>
                <div className="mt-8" data-id="element-2336">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-2337">
                    <UserIcon className="w-5 h-5 mr-2 text-gray-500" data-id="element-2338" />
                    Regular Services
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2339">
                    {mockPropertyManagement.services.map((service, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2340">
                        <div className="flex justify-between items-start" data-id="element-2341">
                          <h4 className="font-medium text-gray-900" data-id="element-2342">
                            {service.type}
                          </h4>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2343">
                            {service.frequency}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500" data-id="element-2344">
                          <p data-id="element-2345">
                            <span className="font-medium" data-id="element-2346">Provider:</span>{' '}
                            {service.provider}
                          </p>
                          <p data-id="element-2347">
                            <span className="font-medium" data-id="element-2348">Contact:</span>{' '}
                            {service.contact}
                          </p>
                          <p data-id="element-2349">
                            <span className="font-medium" data-id="element-2350">Phone:</span>{' '}
                            {service.phone}
                          </p>
                          <p data-id="element-2351">
                            <span className="font-medium" data-id="element-2352">Email:</span>{' '}
                            {service.email}
                          </p>
                          <p data-id="element-2353">
                            <span className="font-medium" data-id="element-2354">Schedule:</span>{' '}
                            {service.schedule}
                          </p>
                          <p data-id="element-2355">
                            <span className="font-medium" data-id="element-2356">Rate:</span> $
                            {service.rate}/{service.frequency.toLowerCase()}
                          </p>
                        </div>
                        <div className="mt-3 text-right" data-id="element-2357">
                          <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2358">
                            <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2359" />
                            Edit
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 text-right" data-id="element-2360">
                    <Button size="sm" onClick={() => {
                setAddItemType('service');
                setShowAddItemModal(true);
              }} data-id="element-2361">
                      <PlusIcon className="w-4 h-4 mr-2" data-id="element-2362" />
                      Add Service
                    </Button>
                  </div>
                </div>
              </div>}
            {/* Vendors & Contacts Section */}
            {activeManagementSection === 'vendors' && <div data-id="element-2363">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-2364">
                  <div className="w-5 h-5 mr-2 text-gray-500" data-id="element-2365" />
                  Vendors & Service Providers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2366">
                  {mockPropertyManagement.vendors.map((vendor, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-2367">
                      <div className="flex items-center mb-2" data-id="element-2368">
                        <div className="p-1.5 rounded-full bg-gray-100 mr-2" data-id="element-2369">
                          <div className="w-4 h-4 text-gray-600" data-id="element-2370" />
                        </div>
                        <h4 className="font-medium text-gray-900" data-id="element-2371">
                          {vendor.type}
                        </h4>
                      </div>
                      <div className="space-y-1 text-sm" data-id="element-2372">
                        <p className="font-medium" data-id="element-2373">{vendor.name}</p>
                        <p data-id="element-2374">
                          <span className="text-gray-500" data-id="element-2375">Contact:</span>{' '}
                          {vendor.contact}
                        </p>
                        <p data-id="element-2376">
                          <span className="text-gray-500" data-id="element-2377">Phone:</span>
                          <a href={`tel:${vendor.phone}`} className="text-blue-600 hover:text-blue-800 ml-1" data-id="element-2378">
                            {vendor.phone}
                          </a>
                        </p>
                        <p data-id="element-2379">
                          <span className="text-gray-500" data-id="element-2380">Email:</span>
                          <a href={`mailto:${vendor.email}`} className="text-blue-600 hover:text-blue-800 ml-1" data-id="element-2381">
                            {vendor.email}
                          </a>
                        </p>
                      </div>
                      <div className="mt-2 text-sm text-gray-600 border-t border-gray-100 pt-2" data-id="element-2382">
                        <p data-id="element-2383">{vendor.notes}</p>
                      </div>
                      <div className="mt-3 text-right" data-id="element-2384">
                        <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2385">
                          <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2386" />
                          Edit
                        </button>
                      </div>
                    </div>)}
                </div>
                <div className="mt-4 text-right" data-id="element-2387">
                  <Button size="sm" onClick={() => {
              setAddItemType('vendor');
              setShowAddItemModal(true);
            }} data-id="element-2388">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-2389" />
                    Add Vendor
                  </Button>
                </div>
              </div>}
            {/* Maintenance Schedule Section */}
            {activeManagementSection === 'maintenance' && <div data-id="element-2390">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-2391">
                  <CalendarDaysIcon className="w-5 h-5 mr-2 text-gray-500" data-id="element-2392" />
                  Maintenance Schedule
                </h3>
                {/* Upcoming Maintenance Alert */}
                <div className="mb-6" data-id="element-2393">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-2394">
                    <h4 className="font-medium text-yellow-800 flex items-center mb-2" data-id="element-2395">
                      <BellIcon className="w-4 h-4 mr-2" data-id="element-2396" />
                      Upcoming Maintenance
                    </h4>
                    <div className="space-y-2" data-id="element-2397">
                      {mockPropertyManagement.maintenanceSchedule.filter(task => isTaskDueSoon(task.nextDue) || isTaskOverdue(task.nextDue)).sort((a, b) => new Date(a.nextDue) - new Date(b.nextDue)).map((task, index) => <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-yellow-100" data-id="element-2398">
                            <div className="flex items-center" data-id="element-2399">
                              <span className={`w-2 h-2 rounded-full mr-2 ${isTaskOverdue(task.nextDue) ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-2400"></span>
                              <span className="font-medium" data-id="element-2401">{task.task}</span>
                            </div>
                            <div className="text-sm" data-id="element-2402">
                              <span className={isTaskOverdue(task.nextDue) ? 'text-red-600 font-medium' : 'text-yellow-600'} data-id="element-2403">
                                {isTaskOverdue(task.nextDue) ? 'Overdue: ' : 'Due: '}
                                {formatDate(task.nextDue)}
                              </span>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto" data-id="element-2404">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-2405">
                    <thead data-id="element-2406">
                      <tr data-id="element-2407">
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2408">
                          Task
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2409">
                          Frequency
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2410">
                          Last Completed
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2411">
                          Next Due
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2412">
                          Assigned To
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2413">
                          Notes
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2414">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-2415">
                      {mockPropertyManagement.maintenanceSchedule.map((task, index) => <tr key={index} className={isTaskOverdue(task.nextDue) ? 'bg-red-50' : isTaskDueSoon(task.nextDue) ? 'bg-yellow-50' : ''} data-id="element-2416">
                            <td className="px-4 py-4 whitespace-nowrap" data-id="element-2417">
                              <div className="font-medium text-gray-900" data-id="element-2418">
                                {task.task}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2419">
                              {task.frequency}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2420">
                              {formatDate(task.lastCompleted)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm" data-id="element-2421">
                              <span className={isTaskOverdue(task.nextDue) ? 'text-red-600 font-medium' : isTaskDueSoon(task.nextDue) ? 'text-yellow-600 font-medium' : 'text-gray-500'} data-id="element-2422">
                                {formatDate(task.nextDue)}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2423">
                              {task.assignedTo}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate" data-id="element-2424">
                              {task.notes}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2425">
                              <button className="text-green-600 hover:text-green-900 mr-3" data-id="element-2426">
                                Complete
                              </button>
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-2427">
                                Edit
                              </button>
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 text-right" data-id="element-2428">
                  <Button size="sm" onClick={() => {
              setAddItemType('maintenance');
              setShowAddItemModal(true);
            }} data-id="element-2429">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-2430" />
                    Add Maintenance Task
                  </Button>
                </div>
              </div>}
            {/* Repairs & Projects Log Section */}
            {activeManagementSection === 'repairs' && <div data-id="element-2431">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-2432">
                  <WrenchIcon className="w-5 h-5 mr-2 text-gray-500" data-id="element-2433" />
                  Repairs & Projects Log
                </h3>
                <div className="space-y-6" data-id="element-2434">
                  {mockPropertyManagement.repairsLog.map((repair, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2435">
                      <div className="bg-gray-50 p-4 flex justify-between items-center" data-id="element-2436">
                        <div data-id="element-2437">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-2438">
                            {repair.id}
                          </span>
                          <span className="font-medium text-gray-900" data-id="element-2439">
                            {repair.description}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2440">
                          {formatDate(repair.dateStarted)} -{' '}
                          {formatDate(repair.dateCompleted)}
                        </div>
                      </div>
                      <div className="p-4" data-id="element-2441">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2442">
                          <div data-id="element-2443">
                            <p className="text-sm text-gray-500" data-id="element-2444">Contractor</p>
                            <p className="font-medium" data-id="element-2445">{repair.contractor}</p>
                            <p className="text-sm text-gray-500" data-id="element-2446">
                              {repair.contactInfo}
                            </p>
                          </div>
                          <div data-id="element-2447">
                            <p className="text-sm text-gray-500" data-id="element-2448">
                              Cost & Payment
                            </p>
                            <p className="font-medium" data-id="element-2449">
                              ${repair.cost.toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-2450">
                              Paid on {formatDate(repair.paymentDate)} via{' '}
                              {repair.paymentMethod}
                            </p>
                          </div>
                        </div>
                        <div className="mb-4" data-id="element-2451">
                          <p className="text-sm text-gray-500" data-id="element-2452">Warranty</p>
                          <p className="font-medium" data-id="element-2453">{repair.warranty}</p>
                        </div>
                        <div className="mb-4" data-id="element-2454">
                          <p className="text-sm text-gray-500" data-id="element-2455">Notes</p>
                          <p className="text-sm text-gray-700" data-id="element-2456">
                            {repair.notes}
                          </p>
                        </div>
                        {repair.photos.length > 0 && <div data-id="element-2457">
                            <p className="text-sm text-gray-500 mb-2" data-id="element-2458">Photos</p>
                            <div className="flex flex-wrap gap-2" data-id="element-2459">
                              {repair.photos.map((photo, photoIndex) => <div key={photoIndex} className="relative w-24 h-24 rounded overflow-hidden" data-id="element-2460">
                                  <img src={photo} alt={`Repair ${repair.id}`} className="w-full h-full object-cover" data-id="element-2461" />
                                </div>)}
                            </div>
                          </div>}
                        <div className="mt-4 text-right" data-id="element-2462">
                          <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2463">
                            <PencilIcon className="w-3 h-3 inline mr-1" data-id="element-2464" />
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-4 text-right" data-id="element-2465">
                  <Button size="sm" onClick={() => {
              setAddItemType('repair');
              setShowAddItemModal(true);
            }} data-id="element-2466">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-2467" />
                    Add Repair/Project
                  </Button>
                </div>
              </div>}
          </div>}
      </div>
      {/* Add Item Modal */}
      {showAddItemModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2468">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-id="element-2469">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2470">
              {addItemType ? `Add New ${addItemType.charAt(0).toUpperCase() + addItemType.slice(1)}` : 'Add New Item'}
            </h3>
            {/* Form content would go here - different based on addItemType */}
            <div className="mt-4 text-center text-gray-500" data-id="element-2471">
              <p data-id="element-2472">
                Form fields would be shown here based on the selected item type.
              </p>
              <p className="mt-2" data-id="element-2473">
                This is a placeholder for the actual form implementation.
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-2474">
              <Button variant="outline" type="button" onClick={() => setShowAddItemModal(false)} data-id="element-2475">
                Cancel
              </Button>
              <Button type="button" onClick={() => setShowAddItemModal(false)} data-id="element-2476">
                Save
              </Button>
            </div>
          </div>
        </div>}
      {/* Investment Strategy Modal */}
      {showStrategyModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2477">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4" data-id="element-2478">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2479">
              Set Investment Strategy
            </h3>
            <form onSubmit={handleStrategySubmit} data-id="element-2480">
              <div className="mb-4" data-id="element-2481">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2482">
                  Investment Strategy
                </label>
                <select className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" value={investmentStrategy} onChange={e => setInvestmentStrategy(e.target.value)} data-id="element-2483">
                  <option value="Long-Term Hold" data-id="element-2484">Long-Term Hold</option>
                  <option value="Short-Term Flip" data-id="element-2485">Short-Term Flip</option>
                </select>
              </div>
              <div className="mb-4" data-id="element-2486">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2487">
                  Expected Hold Period
                </label>
                <select className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" value={expectedHoldPeriod} onChange={e => setExpectedHoldPeriod(e.target.value)} data-id="element-2488">
                  <option value="Less than 1 year" data-id="element-2489">Less than 1 year</option>
                  <option value="1-2 years" data-id="element-2490">1-2 years</option>
                  <option value="2-3 years" data-id="element-2491">2-3 years</option>
                  <option value="3-5 years" data-id="element-2492">3-5 years</option>
                  <option value="5-7 years" data-id="element-2493">5-7 years</option>
                  <option value="7-10 years" data-id="element-2494">7-10 years</option>
                  <option value="10+ years" data-id="element-2495">10+ years</option>
                </select>
              </div>
              {investmentStrategy === 'Long-Term Hold' ? <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2496">
                  <div data-id="element-2497">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2498">
                      Annual Cash Flow ($)
                    </label>
                    <input type="text" className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. 50000" data-id="element-2499" />
                  </div>
                  <div data-id="element-2500">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2501">
                      Cap Rate (%)
                    </label>
                    <input type="text" className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. 5.5" data-id="element-2502" />
                  </div>
                </div> : <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2503">
                  <div data-id="element-2504">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2505">
                      Projected Profit ($)
                    </label>
                    <input type="text" className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. 100000" data-id="element-2506" />
                  </div>
                  <div data-id="element-2507">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2508">
                      Projected ROI (%)
                    </label>
                    <input type="text" className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g. 15.5" data-id="element-2509" />
                  </div>
                </div>}
              <div className="flex justify-end space-x-3" data-id="element-2510">
                <Button variant="outline" type="button" onClick={() => setShowStrategyModal(false)} data-id="element-2511">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-2512">Save Strategy</Button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};
export default PropertyDetail;